import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import { Skeleton } from "../../../Components/Needed";
import { useDirty } from "../unsaved";

const METHODS = [
    { key: "aadhaar", label: "Aadhaar verification", hint: "Require Aadhaar KYC before a customer can book." },
    { key: "dl", label: "Driving licence verification", hint: "Require a verified DL before a customer can book." },
    { key: "third", label: "Third document", hint: "Require one more identity document (voter ID / passport / PAN)." },
];

const THIRD_DOC_TYPES = ["voter", "passport", "pan"];

const VerificationTab = () => {
    const [loading, setLoading] = useState(true);
    const [methods, setMethods] = useState({ aadhaar: false, dl: false, third: false });
    const [savingMethod, setSavingMethod] = useState(null);
    const [allowedTypes, setAllowedTypes] = useState([]);
    const [expiryGraceDays, setExpiryGraceDays] = useState("0");
    const [maxAttempts, setMaxAttempts] = useState("3");
    // KYC v2: the rules' numbers (admin only).
    const [minAge, setMinAge] = useState("21");
    const [minLicenceMonths, setMinLicenceMonths] = useState("6");
    const [validityMonths, setValidityMonths] = useState("12");
    // KYC v2 release switches: each off means the behaviour before KYC v2.
    const [kycV2, setKycV2] = useState({ newRules: false, overrideChange: false, expiryHold: false });
    const [savingPolicy, setSavingPolicy] = useState(false);
    // The policy as last loaded, for the unsaved-changes bar (the method switches save on their own).
    const [savedPolicy, setSavedPolicy] = useState({ allowedTypes: [], expiryGraceDays: "0", maxAttempts: "3", minAge: "21", minLicenceMonths: "6", validityMonths: "12", kycV2: { newRules: false, overrideChange: false, expiryHold: false } });

    const load = useCallback(async () => {
        setLoading(true);
        const response = await Handler({ method: "GET", url: "/admin/business/site/verification/view" });
        if (response?.success) {
            const data = response?.data?.data || {};
            setMethods({ aadhaar: !!data.aadhaar, dl: !!data.dl, third: !!data.third });
            const policy = { allowedTypes: Array.isArray(data.thirdDocumentAllowedTypes) ? data.thirdDocumentAllowedTypes : [], expiryGraceDays: String(data.expiryGraceDays ?? 0), maxAttempts: String(data.maxAttemptsPerDay ?? 3), minAge: String(data.minAge ?? 21), minLicenceMonths: String(data.minLicenceMonths ?? 6), validityMonths: String(data.validityMonths ?? 12), kycV2: { newRules: data.kycV2?.newRules === true, overrideChange: data.kycV2?.overrideChange === true, expiryHold: data.kycV2?.expiryHold === true } };
            setAllowedTypes(policy.allowedTypes);
            setExpiryGraceDays(policy.expiryGraceDays);
            setMaxAttempts(policy.maxAttempts);
            setMinAge(policy.minAge);
            setMinLicenceMonths(policy.minLicenceMonths);
            setValidityMonths(policy.validityMonths);
            setKycV2(policy.kycV2);
            setSavedPolicy(policy);
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggleMethod = async (key) => {
        const nextValue = !methods[key];
        setSavingMethod(key);
        const response = await Handler({
            method: "patch",
            url: "/admin/business/site/verification",
            data: { verification: key, verificationStatus: nextValue },
        });
        if (response?.success) {
            setMethods((prev) => ({ ...prev, [key]: nextValue }));
            toast.success(response?.data?.message || "Updated");
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update"));
        }
        setSavingMethod(null);
    };

    const toggleAllowedType = (type) => {
        setAllowedTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
    };

    const onSavePolicy = async () => {
        if (methods.third && allowedTypes.length === 0) {
            toast.error("Select at least one accepted document type");
            return;
        }
        const graceDays = Number(expiryGraceDays);
        if (Number.isNaN(graceDays) || graceDays < 0) {
            toast.error("Expiry grace days must be a non-negative number");
            return;
        }
        const attempts = Number(maxAttempts);
        if (!Number.isInteger(attempts) || attempts < 1 || attempts > 20) {
            toast.error("Attempts per day must be a whole number from 1 to 20");
            return;
        }
        const age = Number(minAge);
        if (!Number.isInteger(age) || age < 18 || age > 30) {
            toast.error("Minimum age must be a whole number from 18 to 30");
            return;
        }
        const licenceMonths = Number(minLicenceMonths);
        if (!Number.isInteger(licenceMonths) || licenceMonths < 0 || licenceMonths > 24) {
            toast.error("Licence months must be a whole number from 0 to 24");
            return;
        }
        const validity = Number(validityMonths);
        if (!Number.isInteger(validity) || validity < 1 || validity > 60) {
            toast.error("Validity must be a whole number of months from 1 to 60");
            return;
        }
        setSavingPolicy(true);
        const response = await Handler({
            method: "patch",
            url: "/admin/business/site/verification/policy",
            data: { thirdDocumentAllowedTypes: allowedTypes, expiryGraceDays: graceDays, maxAttemptsPerDay: attempts, minAge: age, minLicenceMonths: licenceMonths, validityMonths: validity, kycV2 },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Verification policy updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update verification policy"));
        }
        setSavingPolicy(false);
    };

    useDirty("verification-policy", { allowedTypes: [...allowedTypes].sort(), expiryGraceDays, maxAttempts, minAge, minLicenceMonths, validityMonths, kycV2 }, { ...savedPolicy, allowedTypes: [...savedPolicy.allowedTypes].sort() }, onSavePolicy, () => {
        setAllowedTypes(savedPolicy.allowedTypes);
        setExpiryGraceDays(savedPolicy.expiryGraceDays);
        setMaxAttempts(savedPolicy.maxAttempts);
        setMinAge(savedPolicy.minAge);
        setMinLicenceMonths(savedPolicy.minLicenceMonths);
        setValidityMonths(savedPolicy.validityMonths);
        setKycV2(savedPolicy.kycV2);
    });

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="40%" className="mb-3" />
                <Skeleton height={38} className="mb-3" />
                <Skeleton height={38} className="mb-3" />
            </div>
        );
    }

    return (
        <>
            <div className="settings-card mb-4">
                <h5 className="fw-semibold mb-3">Customer Verification Methods</h5>
                <p className="text-muted small mb-4">Each toggle saves immediately.</p>

                {METHODS.map((method) => (
                    <div className="settings-toggle-row" key={method.key}>
                        <div>
                            <div className="fw-semibold">{method.label}</div>
                            <div className="text-muted small">{method.hint}</div>
                        </div>
                        <div className="form-check form-switch fs-4 m-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                aria-label={method.label}
                                checked={!!methods[method.key]}
                                disabled={savingMethod === method.key}
                                onChange={() => toggleMethod(method.key)}
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div className="settings-card">
                <h5 className="fw-semibold mb-3">Third Document Policy</h5>
                <p className="text-muted small mb-4">Only applies while "Third document" above is enabled.</p>

                <div className="mb-4">
                    <label className="form-label d-block">Accepted document types</label>
                    <div className="d-flex flex-wrap gap-3">
                        {THIRD_DOC_TYPES.map((type) => (
                            <div className="form-check" key={type}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`third-doc-${type}`}
                                    checked={allowedTypes.includes(type)}
                                    onChange={() => toggleAllowedType(type)}
                                />
                                <label className="form-check-label text-capitalize" htmlFor={`third-doc-${type}`}>{type}</label>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="col-12 col-md-4 mb-4">
                    <label className="form-label" htmlFor="verification-grace-days">Expiry grace period (days)</label>
                    <input
                        id="verification-grace-days"
                        type="number"
                        min="0"
                        className="form-control"
                        value={expiryGraceDays}
                        onChange={(e) => setExpiryGraceDays(e.target.value)}
                    />
                    <div className="form-text">How many days after a document expires it's still accepted.</div>
                </div>

                <div className="col-12 col-md-4 mb-4">
                    <label className="form-label" htmlFor="verification-max-attempts">KYC attempts per document per day</label>
                    <input
                        id="verification-max-attempts"
                        type="number"
                        min="1"
                        max="20"
                        step="1"
                        className="form-control"
                        value={maxAttempts}
                        onChange={(e) => setMaxAttempts(e.target.value)}
                    />
                    <div className="form-text">After this many tries in a day (Indian time), the customer must wait until midnight. Tries that failed because the verification service was down don't count.</div>
                </div>

                <h6 className="fw-semibold mt-2 mb-3">KYC rules</h6>
                <div className="col-12 col-md-4 mb-4">
                    <label className="form-label" htmlFor="verification-min-age">Minimum age</label>
                    <input id="verification-min-age" type="number" min="18" max="30" step="1" className="form-control" value={minAge} onChange={(e) => setMinAge(e.target.value)} />
                    <div className="form-text">An Aadhaar or licence with a date of birth under this age is rejected automatically. An admin, or staff with the override permission, can send it back to review. Default 21.</div>
                </div>

                <div className="col-12 col-md-4 mb-4">
                    <label className="form-label" htmlFor="verification-licence-months">Licence: minimum months left</label>
                    <input id="verification-licence-months" type="number" min="0" max="24" step="1" className="form-control" value={minLicenceMonths} onChange={(e) => setMinLicenceMonths(e.target.value)} />
                    <div className="form-text">A driving licence with fewer whole calendar months left is rejected automatically (exactly this many passes). Default 6.</div>
                </div>

                <div className="col-12 col-md-4 mb-4">
                    <label className="form-label" htmlFor="verification-validity-months">How long an approved document stays valid (months)</label>
                    <input id="verification-validity-months" type="number" min="1" max="60" step="1" className="form-control" value={validityMonths} onChange={(e) => setValidityMonths(e.target.value)} />
                    <div className="form-text">After this, the customer must verify the document again. Applies to documents approved from now on. Default 12.</div>
                </div>

                <h6 className="fw-semibold mt-2 mb-1">KYC v2 release switches</h6>
                <div className="form-text mb-3">All three start off, so nothing changes for customers until you turn them on. Turn them on one at a time, in this order, and check the KYC log between steps. Turning one off returns that part to the old behaviour straight away.</div>
                {[
                    ["newRules", "New rules: minimum age and licence months", "On: the age and licence numbers above apply. Off: age 18 and licence 1 month, as before."],
                    ["overrideChange", "Override sends a document back to review", "On: an override sends a rejected document back to Pending and a reviewer cannot reverse a rule rejection. Off: an override approves the document directly, as before."],
                    ["expiryHold", "One-year validity clock", "On: documents expire after the validity above, customers get reminders, and expired KYC blocks booking and trip start. Off: no clock, no reminders and no hold from the date."],
                ].map(([key, title, help]) => (
                    <div className="form-check form-switch mb-3" key={key}>
                        <input className="form-check-input" type="checkbox" role="switch" id={`verification-kycv2-${key}`} checked={!!kycV2[key]} onChange={(e) => setKycV2((current) => ({ ...current, [key]: e.target.checked }))} />
                        <label className="form-check-label" htmlFor={`verification-kycv2-${key}`}>{title}</label>
                        <div className="form-text">{help}</div>
                    </div>
                ))}

                <div className="settings-card-actions">
                    <button type="button" className="btn btn-primary" onClick={onSavePolicy} disabled={savingPolicy}>
                        {savingPolicy ? "Saving..." : "Save policy"}
                    </button>
                </div>
            </div>
        </>
    );
};

export default VerificationTab;
